import { useRef, useState } from 'react';
import { FileX, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import { ErrorDialog } from '@/shared/components/ui/status-dialog';
import { cn } from '@/shared/lib/cn';
import { useIsReadOnly } from '@/shared/auth/SessionProvider';
import {
  uploadDocument,
  type EntityType,
  type UploadDocumentResult,
} from '@/shared/api/documents';

/**
 * Reusable drag-and-drop + browse-button file uploader. Submits a
 * single file to `POST /api/documents/upload` with progress reporting.
 *
 * Wraps the raw `<input type="file">` in a styled drop zone. The
 * server re-validates size/MIME so this is UX polish, not security.
 */

/**
 * Hard cap per file. Kept below Vercel's 4.5 MB serverless body limit with
 * headroom for the multipart envelope. If we ever move to direct-to-Graph
 * uploads this can be raised.
 */
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;
const MAX_UPLOAD_MB = 4;

const ACCEPT_MAP = {
  pdf: { accept: 'application/pdf', label: 'PDF' },
  'pdf-image': {
    accept: 'application/pdf,image/png,image/jpeg',
    label: 'PDF o imagen',
  },
} as const;

export type FileUploaderProps = {
  entityType: EntityType;
  entityId: string;
  onUploaded: (result: UploadDocumentResult) => void;
  accept?: keyof typeof ACCEPT_MAP;
  disabled?: boolean;
  /** Compact single-line variant — drops the drag-and-drop box, just shows the button. */
  compact?: boolean;
  /** Shown above the drop zone (e.g. "Factura"). */
  label?: string;
  /** Shown while nothing is uploaded yet — defaults to "Ningún archivo cargado". */
  emptyHint?: string;
  /**
   * Explicit write authorization that OVERRIDES the global read-only role gate.
   * Pass this when the caller has its own per-record authorization (e.g. a
   * marketing user managing its OWN venta's comprobante). Undefined → fall back
   * to the role-based `isReadOnly` gate.
   */
  canWrite?: boolean;
};

export function FileUploader({
  entityType,
  entityId,
  onUploaded,
  accept = 'pdf-image',
  disabled,
  compact,
  label,
  emptyHint = 'Ningún archivo cargado',
  canWrite,
}: FileUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const roleReadOnly = useIsReadOnly();
  // Explicit `canWrite` wins; otherwise the role gate decides.
  const isReadOnly = canWrite === undefined ? roleReadOnly : !canWrite;

  const acceptAttr = ACCEPT_MAP[accept].accept;
  const acceptLabel = ACCEPT_MAP[accept].label;

  if (isReadOnly) {
    if (compact) {
      return (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <FileX className="h-3.5 w-3.5" aria-hidden />
          Sin archivo cargado
        </span>
      );
    }
    return (
      <div className="space-y-1.5">
        {label && (
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
        )}
        <div className="flex min-h-[72px] items-center justify-center gap-2 rounded-xl border border-dashed border-muted-foreground/30 bg-card/40 px-4 py-3 text-sm text-muted-foreground">
          <FileX className="h-4 w-4" aria-hidden />
          <span>{emptyHint}</span>
        </div>
      </div>
    );
  }

  const startUpload = async (file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      setSizeError(
        `El archivo pesa ${(file.size / (1024 * 1024)).toFixed(1)} MB y el tope permitido es ${MAX_UPLOAD_MB} MB. ` +
          'Comprimí el archivo antes de volver a intentarlo.',
      );
      return;
    }
    const allowedTypes = acceptAttr.split(',');
    if (!allowedTypes.some((t) => file.type === t.trim())) {
      toast.error('Tipo de archivo no soportado', {
        description: `Se aceptan: ${acceptLabel}.`,
      });
      return;
    }
    setUploading(true);
    setProgress(0);
    try {
      const result = await uploadDocument({
        entityType,
        entityId,
        file,
        onProgress: (p) => {
          if (p.total > 0) setProgress(p.loaded / p.total);
        },
      });
      onUploaded(result);
      toast.success('Archivo cargado', { description: result.fileName });
    } catch (err) {
      toast.error('No se pudo subir el archivo', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setUploading(false);
      setProgress(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onFilePicked = (files: FileList | null | undefined) => {
    if (!files || files.length === 0) return;
    void startUpload(files[0]);
  };

  const sizeErrorDialog = (
    <ErrorDialog
      open={sizeError !== null}
      message={sizeError ?? ''}
      title="Archivo demasiado grande"
      onClose={() => setSizeError(null)}
    />
  );

  if (compact) {
    return (
      <>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={acceptAttr}
            disabled={disabled || uploading}
            onChange={(e) => onFilePicked(e.target.files)}
            className="hidden"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            className="gap-2"
          >
            {uploading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-3.5 w-3.5" aria-hidden />
            )}
            {uploading ? `Subiendo ${Math.round((progress ?? 0) * 100)}%` : 'Subir'}
          </Button>
        </div>
        {sizeErrorDialog}
      </>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </p>
      )}
      <div
        className={cn(
          'relative flex min-h-[72px] cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-3 text-center transition-colors',
          dragOver
            ? 'border-foreground bg-foreground/5'
            : 'border-muted-foreground/30 bg-card/40 hover:bg-card/70',
          (disabled || uploading) && 'pointer-events-none opacity-60',
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onFilePicked(e.dataTransfer?.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={acceptAttr}
          disabled={disabled || uploading}
          onChange={(e) => onFilePicked(e.target.files)}
          className="hidden"
        />
        {uploading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden />
            <span className="text-sm text-muted-foreground">
              Subiendo {Math.round((progress ?? 0) * 100)}%
            </span>
          </>
        ) : (
          <>
            <Upload className="h-5 w-5 text-muted-foreground" aria-hidden />
            <div className="text-left">
              <p className="text-sm font-medium text-foreground">
                Arrastrá un archivo o hacé clic para subir
              </p>
              <p className="text-xs text-muted-foreground">
                {emptyHint} · {acceptLabel} · máx. {MAX_UPLOAD_MB} MB
              </p>
            </div>
          </>
        )}
      </div>
      {sizeErrorDialog}
    </div>
  );
}
